import { LIST_ROLES_CLINIC, ROLE, HAS_LOGIN_BANNER_ORGANIZATION_SLUGS } from '@/shared/config';
import _ from 'lodash';
import { storage, STORAGE_KEYS } from '../storage';
import type { StorageKey } from '../storage';

// Accessors are untyped because they used to read straight off the Storage object, and consumers
// rely on that `any` (and on a missing key being falsy, not on null vs undefined).
/**
 * Reads one auth-related value from the app's browser storage.
 * Delegates straight to storage.get for the given key without any parsing or defaulting.
 *
 * @param key - Storage key to read.
 * @returns The stored value, or a falsy value when the key is missing.
 */
const read = (key: StorageKey): any => storage.get(key);

/**
 * Looks up the signed-in user's id from the user store once a selector has been registered.
 * Starts as a stub returning undefined and is replaced by registerAuthStoreSelector.
 *
 * @returns The current user id, or undefined before a selector is registered.
 */
let getMeIdFromStore: () => string | undefined = () => undefined;
/**
 * Connects the auth helper to the user store so it can read the current user id without importing the store. Saves the supplied selector into the module-level getMeIdFromStore reference.
 *
 * @param selector - Selector returning the signed-in user id from the store.
 * @returns Nothing.
 */
export const registerAuthStoreSelector = (selector: () => string | undefined) => {
  getMeIdFromStore = selector;
};

const auth = {
  saveShareData: (shareData: Partial<Record<StorageKey, unknown>>) => {
    Object.entries(shareData).forEach(([key, value]) => storage.set(key as StorageKey, value));
  },

  setAuthorizedToken: (authorizedToken: any) => {
    storage.set(STORAGE_KEYS.authorizedToken, authorizedToken);
  },

  authorizedToken: () => read(STORAGE_KEYS.authorizedToken),

  setAdminOrSalesFacilityId: (adminOrSalesFacilityId: any) => {
    storage.set(STORAGE_KEYS.adminOrSalesFacilityId, adminOrSalesFacilityId);
  },

  adminOrSalesFacilityId: () => read(STORAGE_KEYS.adminOrSalesFacilityId),

  setUserData: (me: any) => {
    storage.set(STORAGE_KEYS.isSuccess, !_.isEmpty(me));
    storage.set(STORAGE_KEYS.userId, me?.id);
    storage.set(STORAGE_KEYS.username, me?.username);
    storage.set(STORAGE_KEYS.firstName, me?.firstName);
    storage.set(STORAGE_KEYS.lastName, me?.lastName);
    storage.set(STORAGE_KEYS.email, me?.email);
    storage.set(STORAGE_KEYS.phone1, me?.contact?.phone1);
    storage.set(STORAGE_KEYS.country, me?.contact?.country);
    storage.setJson(STORAGE_KEYS.roles, me?.roles);
    storage.set(STORAGE_KEYS.freshChatRestoreId, me?.freshChatRestoreId);
    storage.set(STORAGE_KEYS.willViewTutorialLater, me?.willViewTutorialLater);
    storage.set(STORAGE_KEYS.willShowWhatNewPopup, me?.willViewTutorialLater);
    storage.set(STORAGE_KEYS.canUseAiViewer, me?.canUseAiViewer);
    storage.set(STORAGE_KEYS.isEmailVerified, me?.isEmailVerified);
    storage.set(STORAGE_KEYS.title, me?.title);
    storage.setJson(STORAGE_KEYS.organization, me?.organization || {});
    storage.setJson(STORAGE_KEYS.userInfo, {
      firstName: me?.firstName,
      lastName: me?.lastName,
      title: me?.title,
      includeContactToReport: me?.includeContactToReport,
      contact: {
        phone1: me?.contact?.phone1,
        country: me?.contact?.country,
        address: me?.contact?.address,
        city: me?.contact?.city,
        state: me?.contact?.state,
        zip: me?.contact?.zip,
      },
    });
  },

  saveLocale: (language: any) => {
    storage.set(STORAGE_KEYS.language, language);
  },

  getLocale: () => read(STORAGE_KEYS.language) || 'en',

  getUserInfo: () => storage.getJson<any>(STORAGE_KEYS.userInfo, {}),

  setAccessToken: (accessToken: any) => {
    storage.set(STORAGE_KEYS.accessToken, accessToken);
  },

  accessToken: () => read(STORAGE_KEYS.accessToken),

  setUserId: (userId: any) => {
    storage.set(STORAGE_KEYS.userId, userId);
  },

  removeUserId: () => {
    storage.remove(STORAGE_KEYS.userId);
  },

  userId: () => getMeIdFromStore() || read(STORAGE_KEYS.userId),

  userTitle: () => read(STORAGE_KEYS.title),

  firstName: () => read(STORAGE_KEYS.firstName),

  setFirstName: (name: any) => {
    storage.set(STORAGE_KEYS.firstName, name);
  },

  lastName: () => read(STORAGE_KEYS.lastName),

  setLastName: (name: any) => {
    storage.set(STORAGE_KEYS.lastName, name);
  },

  username: () => read(STORAGE_KEYS.username),

  email: () => {
    const email = read(STORAGE_KEYS.email);
    return email === 'null' ? null : email;
  },

  phone1: () => {
    const phone1 = read(STORAGE_KEYS.phone1);
    return phone1 === 'null' ? null : phone1;
  },

  country: () => {
    const country = read(STORAGE_KEYS.country);
    return country === 'null' ? null : country;
  },

  setEmail: (email: any) => {
    storage.set(STORAGE_KEYS.email, email);
  },

  setRoles: (roles: any) => {
    storage.setJson(STORAGE_KEYS.roles, roles);
  },

  roles: () => storage.getJson<any>(STORAGE_KEYS.roles, []),

  freshChatRestoreId: () => {
    const freshChatRestoreId = read(STORAGE_KEYS.freshChatRestoreId);
    return freshChatRestoreId !== 'null' ? freshChatRestoreId : null;
  },

  setFreshChatRestoreId: (freshChatRestoreId: any) => {
    storage.set(STORAGE_KEYS.freshChatRestoreId, freshChatRestoreId);
  },

  isEmailVerified: () => read(STORAGE_KEYS.isEmailVerified) === 'true',

  setIsEmailVerified: (isEmailVerified: any) => {
    storage.set(STORAGE_KEYS.isEmailVerified, isEmailVerified);
  },

  getWillViewTutorialLater: () => {
    const willViewTutorialLater = read(STORAGE_KEYS.willViewTutorialLater);
    return willViewTutorialLater && willViewTutorialLater !== 'undefined'
      ? willViewTutorialLater
      : 'false';
  },

  saveWillViewTutorialLater: (willViewTutorialLater: any) => {
    storage.set(STORAGE_KEYS.willViewTutorialLater, willViewTutorialLater);
  },

  getWillShowWhatNewPopup: () => {
    const willShowWhatNewPopup = read(STORAGE_KEYS.willShowWhatNewPopup);
    return willShowWhatNewPopup && willShowWhatNewPopup !== 'undefined'
      ? willShowWhatNewPopup
      : 'false';
  },

  saveWillShowWhatNewPopup: (willShowWhatNewPopup: any) => {
    storage.set(STORAGE_KEYS.willShowWhatNewPopup, willShowWhatNewPopup);
  },

  setUserFacilityId: (facilityId: any) => {
    storage.set(STORAGE_KEYS.facilityId, facilityId);
  },

  userFacilityId: () => read(STORAGE_KEYS.facilityId),

  setUserGroupId: (groupId: any) => {
    storage.set(STORAGE_KEYS.groupId, groupId);
  },

  userGroupId: () => read(STORAGE_KEYS.groupId),

  setIsShowLatestVersionModal: (isShowLatestVersionModal: any) => {
    storage.set(STORAGE_KEYS.isShowLatestVersionModal, isShowLatestVersionModal);
  },

  isShowLatestVersionModal: () => read(STORAGE_KEYS.isShowLatestVersionModal) === 'true',

  isShowLoginByBanner: () => read(STORAGE_KEYS.isShowLoginByBanner) === 'true',

  setIsShowLoginByBanner: (isShowLoginByBanner: any) => {
    storage.set(STORAGE_KEYS.isShowLoginByBanner, isShowLoginByBanner);
  },

  canUseAiViewer: () => read(STORAGE_KEYS.canUseAiViewer) === 'true',

  isClinicTechnician: () => _.includes(auth.roles(), ROLE.CLINIC_TECHNICIAN),

  isClinicPhysician: () => _.includes(auth.roles(), ROLE.CLINIC_PHYSICIAN),

  isClinic: () => _.intersection(auth.roles(), LIST_ROLES_CLINIC).length > 0,

  setIsLoginThroughAdmin: (isLoginThroughAdmin: any) => {
    storage.set(STORAGE_KEYS.isLoginThroughAdmin, isLoginThroughAdmin);
  },

  setIsLoginThroughSales: (isLoginThroughSales: any) => {
    storage.set(STORAGE_KEYS.isLoginThroughSales, isLoginThroughSales);
  },

  setIsLoginWithBiocarePortal: (isLoginWithBiocarePortal: any) => {
    storage.set(STORAGE_KEYS.isLoginWithBiocarePortal, isLoginWithBiocarePortal);
  },

  isLoginThroughAdmin: () => read(STORAGE_KEYS.isLoginThroughAdmin) === 'true',

  isLoginThroughSales: () => read(STORAGE_KEYS.isLoginThroughSales) === 'true',

  isAdminOrSales: () => auth.isLoginThroughAdmin() || auth.isLoginThroughSales(),

  isLoginWithMicrosoftAccount: () => read(STORAGE_KEYS.isLoginWithMicrosoftAccount) === 'true',

  isLoginWithBiocarePortal: () => read(STORAGE_KEYS.isLoginWithBiocarePortal) === 'true',

  userOrganization: () => storage.getJson<any>(STORAGE_KEYS.organization, {}),

  hasMarketingBanner: () => !auth.isLoginWithMicrosoftAccount(),

  hasLogoBanner: () => HAS_LOGIN_BANNER_ORGANIZATION_SLUGS.includes(auth.userOrganization()?.slug),

  getLoginFrom: () => read(STORAGE_KEYS.loginFrom),

  setLoginFrom: (loginFrom: any) => {
    storage.set(STORAGE_KEYS.loginFrom, loginFrom);
  },

  removeLoginFrom: () => {
    storage.remove(STORAGE_KEYS.loginFrom);
  },

  getIntegrationBy: () => read(STORAGE_KEYS.integrationBy),

  setIntegrationBy: (integrationBy: any) => {
    storage.set(STORAGE_KEYS.integrationBy, integrationBy);
  },

  removeIntegrationBy: () => {
    storage.remove(STORAGE_KEYS.integrationBy);
  },

  logout: () => {
    storage.clear();
  },

  logoutExceptUsername: () => {
    storage.clearExcept([STORAGE_KEYS.firstName, STORAGE_KEYS.lastName]);
  },

  logoutExceptPathname: () => {
    storage.clearExcept([STORAGE_KEYS.pathname]);
  },
};

export default auth;
